import React, { useState, useEffect } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { styled } from '@moonpig/launchpad-utils'
import { TextInput } from '@moonpig/launchpad-forms'
import {
  Box,
  TertiaryButton,
  Alert,
  Heading,
} from '@moonpig/launchpad-components'
import { PasswordVisibilityEyecon } from '@moonpig/web-shared-components'
import { FormStep, ValidationError } from './types'
import { useAuthenticationLocaleText } from '../../text-localisation'
import { PasswordRules } from './PasswordRules'

const passwordRegexPattern = /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9]).{8,74}$/

const StyledTertiaryButton = styled(TertiaryButton)`
  font-weight: 300;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

export const PasswordField: React.FC<{
  validationErrors: ValidationError[]
  handleForgottenPassword: () => void
}> = ({ validationErrors, handleForgottenPassword }) => {
  const localiseText = useAuthenticationLocaleText()
  const [showPassword, setShowPassword] = useState(false)
  const [password, setPassword] = useState('')
  const { control, getValues } = useFormContext()
  const { formStep } = getValues()

  useEffect(() => {
    setPassword('')
  }, [formStep])

  const customOnPasswordChange = (
    onChangeEvent: (...event: React.ChangeEvent<HTMLInputElement>[]) => void,
  ) => {
    return (rawEvent: React.ChangeEvent<HTMLInputElement>) => {
      const p = rawEvent.target.value
      setPassword(p)
      onChangeEvent(rawEvent)
    }
  }

  const canShowPasswordField =
    formStep !== FormStep.WELCOME &&
    formStep !== FormStep.SOCIAL_LOGIN &&
    formStep !== FormStep.CODE_LOGIN &&
    formStep !== FormStep.CODE_RECOVERY

  return (
    <Box display={canShowPasswordField ? '' : 'none'}>
      {formStep === FormStep.REGISTER && (
        <StyledAlert variant="info" mb={6}>
          <Heading
            level="h4"
            mb={0}
            typography={{
              xs: 'typeMobileDisplay05',
              md: 'typeDesktopDisplay06',
            }}
          >
            {localiseText('auth_wizard.register.info_create_password_heading')}
          </Heading>
          <span>
            {localiseText('auth_wizard.register.info_create_password_message')}
          </span>
        </StyledAlert>
      )}
      <Controller
        control={control}
        name="password"
        rules={{
          required: {
            value: formStep !== FormStep.WELCOME,
            message:
              formStep === FormStep.REGISTER
                ? localiseText('auth_wizard.password_error')
                : localiseText('auth_wizard.password_required_error'),
          },
          pattern: {
            value: formStep === FormStep.REGISTER ? passwordRegexPattern : /.*/,
            message:
              formStep === FormStep.REGISTER
                ? localiseText('auth_wizard.password_error')
                : localiseText('auth_wizard.password_required_error'),
          },
        }}
        render={({
          field,
          field: { onChange: onFieldChange },
          fieldState: { error },
        }) => (
          <TextInput
            mb={6}
            type={showPassword ? /* istanbul ignore next */ 'text' : 'password'}
            label={localiseText('auth_wizard.password_label')}
            {...field}
            onChange={e => customOnPasswordChange(onFieldChange)(e)}
            error={
              !!(error || validationErrors.find(e => e.field === 'password'))
            }
            helperText={
              error?.message ??
              validationErrors.find(e => e.field === 'password')?.message
            }
            componentEnd={
              <PasswordVisibilityEyecon
                isVisible={showPassword}
                setVisibility={setShowPassword}
              />
            }
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck="false"
            autoComplete="password"
          />
        )}
      />

      {formStep === FormStep.REGISTER && (
        <div style={{ marginBottom: '16px' }}>
          <PasswordRules password={password} />
        </div>
      )}

      <Box
        display={
          formStep === FormStep.LOGIN || formStep === FormStep.ACCOUNT_LINKING
            ? ''
            : 'none'
        }
        mb={6}
      >
        <StyledTertiaryButton
          onClick={handleForgottenPassword}
          type="button"
          inline
        >
          {localiseText('auth_wizard.forgot_password')}
        </StyledTertiaryButton>
      </Box>
    </Box>
  )
}

const StyledAlert = styled(Alert)`
  & > div {
    padding-left: 8px;
    & > svg {
      display: none;
    }
  }
`
